import React, { createContext, useEffect, useReducer, useState } from 'react';


import topApi from '../../components/api/ToPApi';
import { LoginData, LoginResponse, Resource, ResourceResponse, User, UserResponse } from '../../models/interfaces';
import { AuthState, authReducer } from './AuthReducer';
import { json } from 'stream/consumers';
import { useLocation } from 'react-router-dom';

type AuthContextProps = {
    errorMessage: string;
    successMessage: string;
    token: string | null;
    user: User | null;
    status: 'checking' | 'authenticated' | 'not-authenticated';
    resources: Resource[];
    login: (loginData: LoginData) => Promise<User>;
    logout: () => void;
    resetPassword: (username: string) => void;
    changePassword: (lastPassword: string, newPassword: string) => Promise<boolean>;
    removeError: () => void;
    reloadResources: () => Promise<void>;   // menú de nuevo (por ejemplo, al activar una funcionalidad)
}

const authInicialState: AuthState = {
    status: 'checking',
    token: null,
    user: null,
    errorMessage: '',
    successMessage: ''
}



export const AuthContext = createContext({} as AuthContextProps);

export const AuthProvider = ({ children }: any) => {

    const [state, dispatch] = useReducer(authReducer, authInicialState);
    const [resources, setResources] = useState([]);
    const location = useLocation();


    useEffect(() => {
        if (!location.pathname.startsWith('/linkpago/')) {
            checkToken();
        }
    }, [location.pathname])

    const checkToken = async () => {

        const token = await localStorage.getItem('token');

        // No token, no autenticado
        if (!token) return dispatch({ type: 'notAuthenticated' });

        // Hay token

        try {
            const resp = await topApi.get('/auth');
            if (resp.status !== 200) {
                return dispatch({ type: 'notAuthenticated' });
            }

            if (resources.length == 0) {
                topApi.get<ResourceResponse>('/security/getResources').then((res) => {
                    if (res.status == 200) {
                        setResources(res.data.resources);
                    }
                });
            }

            await localStorage.setItem('token', resp.data.token);
            dispatch({
                type: 'signUp',
                payload: {
                    token: resp.data.token,
                    user: resp.data.user
                }
            });
        } catch (error) {
            return dispatch({ type: 'notAuthenticated' });
        }
    }


    const login = async ({ correo, password }: LoginData) => {

        try {
            dispatch({ type: 'isLoging' });

            if (correo.length == 0 || password.length == 0) {
                dispatch({
                    type: 'addError',
                    payload: 'Usuario y/o contraseña son requeridos.'
                });
                return;
            }

            const { data } = await topApi.post<LoginResponse>('/auth/login', { user: correo, password, retry: true });

            if (data.user.tempPassword) {
                dispatch({ type: 'notAuthenticated' });
                await localStorage.setItem('x-token', data.token);
                return data.user;
            }


            dispatch({
                type: 'signUp',
                payload: {
                    token: data.token,
                    user: data.user
                }
            });


            topApi.get<ResourceResponse>('/security/getResources').then((res) => {
                if (res.status == 200) {
                    setResources(res.data.resources);
                }
            });



            return data.user;

        } catch (error) {
            dispatch({
                type: 'addError',
                payload: error.response.data.error
                    || 'Información incorrecta'
            })
        }
    };


    const logout = async () => {
        await localStorage.removeItem('token');
        dispatch({ type: 'logout' });
    };

    const removeError = () => {
        dispatch({ type: 'removeError' });
    };

    const resetPassword = async (username: string) => {
        try {

            if (username.length == 0) {
                dispatch({
                    type: 'addError',
                    payload: 'Usuario requerido para recuperar contraseña.'
                });
                return;
            }

            dispatch({ type: 'isLoging' });


            const headers = {
                'x-username': username
            };

            const resp = await topApi.put('/auth/resetPassword', null, { headers });
            if (resp.status !== 200) {
                dispatch({
                    type: 'addError',
                    payload: resp.data.error
                        || 'Ocurrió un error de comunicación, intente de nuevo.'
                });
                return;
            }

            dispatch({
                type: 'addMessage',
                payload: 'Se ha enviado un correo para reestablecer su contraseña.'
            });

        } catch (error) {
            dispatch({
                type: 'addError',
                payload: error?.response?.data?.error
                    || 'Ocurrió un error de comunicación, intente de nuevo.'
            })

        }
    };

    const changePassword = async (lastPassword: string, newPassword: string) => {
        try {

            if (newPassword.length == 0) {
                dispatch({
                    type: 'addError',
                    payload: 'La nueva contraseña es requerida'
                });
                return;
            }

            dispatch({ type: 'isLoging' });

            const token = await localStorage.getItem('x-token');

            const headers = {
                'x-authorization': token
            };

            const body = {
                "lastPassword": lastPassword,
                "newPassword": newPassword
            }

            const resp = await topApi.post('/auth/changePassword', body, { headers });
            if (resp.status !== 200) {
                dispatch({
                    type: 'addError',
                    payload: resp.data.error
                        || 'Ocurrió un error de comunicación, intente de nuevo.'
                });
                return false;
            }
            await localStorage.removeItem('x-token');

            dispatch({
                type: 'addMessage',
                payload: 'Se ha cambiado la contraseña correctamente.'
            });

            return true;

        } catch (error) {
            dispatch({
                type: 'addError',
                payload: error?.response?.data?.error
                    || 'Ocurrió un error de comunicación, intente de nuevo.'
            })
            return false;
        }
    };


    const reloadResources = async () => {
        const res = await topApi.get<ResourceResponse>('/security/getResources');
        if (res.status == 200) setResources(res.data.resources);
    };


    return (
        <AuthContext.Provider value={{
            ...state,
            resources,
            reloadResources,
            login,
            logout,
            removeError,
            resetPassword,
            changePassword
        }}>
            {children}
        </AuthContext.Provider>
    )

}